const WORKSPACE_KEY = "lwt.pythonWorkspace";
const MAX_FILES = 20;
const MAX_VERSIONS_PER_FILE = 30;
const MAX_VERSION_SOURCE_CHARACTERS = 600_000;

function makeId(prefix) {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export function normalizePythonFilename(value) {
  let name = String(value || "").trim().replace(/\\/g, "/").split("/").pop() || "";
  if (!name) throw new Error("Enter a Python file name.");
  const stem = name.toLowerCase().endsWith(".py") ? name.slice(0, -3) : name;
  const moduleName = stem.replace(/[^A-Za-z0-9_]/g, "_").replace(/^([^A-Za-z_])/, "_$1").slice(0, 61);
  return `${moduleName || "script"}.py`;
}

function normalizeVersion(version, index) {
  if (!version || typeof version.code !== "string") return null;
  return {
    id: String(version.id || `version-${index}`),
    label: String(version.label || `Version ${index + 1}`).slice(0, 48),
    createdAt: Number.isFinite(Number(version.createdAt)) ? Number(version.createdAt) : 0,
    code: version.code.slice(0, 30000),
  };
}

function normalizeFile(file, index) {
  if (!file || typeof file.code !== "string") return null;
  let name;
  try { name = normalizePythonFilename(file.name || `script-${index + 1}.py`); } catch { name = `script-${index + 1}.py`; }
  const versions = Array.isArray(file.versions)
    ? file.versions.map(normalizeVersion).filter(Boolean).slice(0, MAX_VERSIONS_PER_FILE)
    : [];
  return {
    id: String(file.id || `file-${index + 1}`),
    name,
    code: file.code.slice(0, 30000),
    versions,
    updatedAt: Number(file.updatedAt) || 0,
  };
}

export function createPythonWorkspace(code = "") {
  return {
    activeFileId: "main",
    files: [{ id: "main", name: "main.py", code: String(code).slice(0, 30000), versions: [], updatedAt: Date.now() }],
  };
}

export function loadPythonWorkspace(storage, fallbackCode = "") {
  try {
    const parsed = JSON.parse(storage?.getItem(WORKSPACE_KEY) || "null");
    const files = Array.isArray(parsed?.files) ? parsed.files.map(normalizeFile).filter(Boolean).slice(0, MAX_FILES) : [];
    if (files.length) {
      const unique = [];
      const usedNames = new Set();
      for (const file of files) {
      const base = file.name;
        let name = base;
        let suffix = 2;
        while (usedNames.has(name.toLowerCase())) {
          name = `${base.slice(0, -3)}-${suffix}.py`;
          suffix += 1;
        }
        usedNames.add(name.toLowerCase());
        unique.push({ ...file, name });
      }
      const activeFileId = unique.some((file) => file.id === parsed.activeFileId) ? parsed.activeFileId : unique[0].id;
      return { activeFileId, files: unique };
    }
  } catch { /* use the older single-file editor save below */ }
  let legacy = fallbackCode;
  try { legacy = storage?.getItem("lwt.pythonCode") || fallbackCode; } catch { /* storage may be blocked */ }
  return createPythonWorkspace(legacy);
}

export function savePythonWorkspace(storage, workspace) {
  const serialized = JSON.stringify(workspace);
  if (serialized.length > 1_500_000) throw new Error("This project is larger than the browser's local storage limit.");
  storage?.setItem(WORKSPACE_KEY, serialized);
}

export function activePythonFile(workspace) {
  return workspace?.files?.find((file) => file.id === workspace.activeFileId) || workspace?.files?.[0] || null;
}

export function updatePythonFile(workspace, fileId, code) {
  const value = String(code).slice(0, 30000);
  return {
    ...workspace,
    files: workspace.files.map((file) => file.id === fileId ? { ...file, code: value, updatedAt: Date.now() } : file),
  };
}

export function setActivePythonFile(workspace, fileId) {
  if (!workspace.files.some((file) => file.id === fileId)) return workspace;
  return { ...workspace, activeFileId: fileId };
}

export function addPythonFile(workspace, requestedName, code = "# Python script\n") {
  if (workspace.files.length >= MAX_FILES) throw new Error(`A project can contain up to ${MAX_FILES} Python files.`);
  const name = normalizePythonFilename(requestedName);
  if (workspace.files.some((file) => file.name.toLowerCase() === name.toLowerCase())) {
    throw new Error(`${name} already exists in this project.`);
  }
  const file = { id: makeId("file"), name, code: String(code).slice(0, 30000), versions: [], updatedAt: Date.now() };
  return { workspace: { activeFileId: file.id, files: [...workspace.files, file] }, file };
}

export function renamePythonFile(workspace, fileId, requestedName) {
  const name = normalizePythonFilename(requestedName);
  if (workspace.files.some((file) => file.id !== fileId && file.name.toLowerCase() === name.toLowerCase())) {
    throw new Error(`${name} already exists in this project.`);
  }
  return { ...workspace, files: workspace.files.map((file) => file.id === fileId ? { ...file, name } : file) };
}

export function removePythonFile(workspace, fileId) {
  if (workspace.files.length <= 1) throw new Error("A project needs at least one Python file.");
  const files = workspace.files.filter((file) => file.id !== fileId);
  return { activeFileId: fileId === workspace.activeFileId ? files[0].id : workspace.activeFileId, files };
}

export function capturePythonVersion(workspace, fileId, code) {
  const current = String(code).slice(0, 30000);
  let saved = null;
  const files = workspace.files.map((file) => {
    if (file.id !== fileId) return file;
    if (file.versions[0]?.code === current) return file;
    const version = {
      id: makeId("version"),
      label: `Version ${file.versions.length + 1}`,
      createdAt: Date.now(),
      code: current,
    };
    saved = version;
    return { ...file, code: current, updatedAt: Date.now(), versions: [version, ...file.versions].slice(0, MAX_VERSIONS_PER_FILE) };
  });
  const recent = files.flatMap((file) => file.versions.map((version) => ({ fileId: file.id, version })))
    .sort((a, b) => b.version.createdAt - a.version.createdAt);
  const keep = new Set();
  let total = 0;
  for (const item of recent) {
    if (total + item.version.code.length > MAX_VERSION_SOURCE_CHARACTERS) continue;
    keep.add(item.version.id);
    total += item.version.code.length;
  }
  const trimmed = files.map((file) => ({ ...file, versions: file.versions.filter((version) => keep.has(version.id)) }));
  return { workspace: { ...workspace, files: trimmed }, version: saved };
}

export function pythonVersionDiffLines(previous, current) {
  const left = String(previous || "").split("\n");
  const right = String(current || "").split("\n");
  if (left.length * right.length > 250_000) {
    let prefix = 0;
    while (prefix < left.length && prefix < right.length && left[prefix] === right[prefix]) prefix += 1;
    let suffix = 0;
    while (suffix < left.length - prefix && suffix < right.length - prefix && left[left.length - 1 - suffix] === right[right.length - 1 - suffix]) suffix += 1;
    return {
      unchangedBefore: prefix,
      removed: left.slice(prefix, left.length - suffix),
      added: right.slice(prefix, right.length - suffix),
      unchangedAfter: suffix,
      same: prefix === left.length && prefix === right.length,
    };
  }

  const lcs = Array.from({ length: left.length + 1 }, () => new Uint16Array(right.length + 1));
  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      lcs[i][j] = left[i] === right[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const removed = [];
  const added = [];
  let i = 0;
  let j = 0;
  let unchangedBefore = 0;
  let unchangedAfter = 0;
  let changed = false;
  while (i < left.length || j < right.length) {
    if (i < left.length && j < right.length && left[i] === right[j]) {
      if (changed) unchangedAfter += 1;
      else unchangedBefore += 1;
      i += 1;
      j += 1;
    } else if (i < left.length && (j >= right.length || lcs[i + 1][j] >= lcs[i][j + 1])) {
      removed.push(left[i]);
      changed = true;
      unchangedAfter = 0;
      i += 1;
    } else {
      added.push(right[j]);
      changed = true;
      unchangedAfter = 0;
      j += 1;
    }
  }
  return { unchangedBefore, removed, added, unchangedAfter, same: removed.length === 0 && added.length === 0 };
}
